/**
 * The reducer: the only way state changes.
 *
 * reduce(state, envelope) clones the state, validates and applies one
 * command, and returns the new state plus domain events — or a rejection.
 * Pure: no I/O, no clock reads, no Math.random; random choices are seeded.
 */
import { isSeatCommand, type ActionCommand, type Command, type SeatCommand } from './commands.ts';
import { Rng } from './prng.ts';
import { ACT_HANDLERS, NO_SCANNER, scannerBox, type SeatHandler } from './rules/act.ts';
import { Reject, type Actor, type Ctx } from './rules/common.ts';
import { runControl } from './rules/faculty.ts';
import { TEAM_HANDLERS } from './rules/team.ts';
import { runDraftCommand } from './rules/drafts.ts';
import type { DomainEvent, Phase, PublicState, Role, SessionState, TableState } from './types.ts';

export interface Envelope {
  cmd: Command;
  actor: Actor;
  at: number;
}

export type ReduceResult =
  | { ok: true; state: SessionState; events: DomainEvent[] }
  | { ok: false; reason: string; recorded?: { state: SessionState; events: DomainEvent[] }; bug?: boolean };

export const HANDLERS: Record<ActionCommand['type'], SeatHandler> = { ...ACT_HANDLERS, ...TEAM_HANDLERS } as unknown as Record<ActionCommand['type'], SeatHandler>;

const PHASE_WORDS: Partial<Record<Phase, string>> = {
  SETUP: 'The shift has not started.',
  ARRIVALS: 'Look up: arrivals and the event.',
  HUDDLE: 'Huddle — phones down.',
  TICK_OPEN: 'The turn is being played out.',
  LOG: 'The turn is over. Wait for the next one.',
  HANDOVER: 'It is 0700: hand over your patients.',
  DEBRIEF: 'The shift is over.',
  ENDED: 'The shift is over.',
};

/**
 * Validate a seat command without applying it (used by projections and bots).
 * Returns null when legal, else the player-facing reason. While planning, a full scanner
 * is not a refusal: scans beyond capacity are settled by priority when the turn is played.
 */
export function checkSeatCommand(pub: PublicState, tableNo: number, role: Role, cmd: SeatCommand, planning = false): string | null {
  const t = pub.tables.find((x) => x.no === tableNo);
  if (!t) return 'No such table.';
  const st = t.seats[role];
  if (!st) return 'You are not seated.';
  if (cmd.type === 'SET_DRAFT' || cmd.type === 'SET_READY' || cmd.type === 'RESPOND_REFERRAL') return null;
  const h = HANDLERS[cmd.type];
  if (!h) return 'The game did not understand that.';
  if (!h.phases.includes(pub.clock.phase)) return PHASE_WORDS[pub.clock.phase] ?? 'Not now.';
  if (pub.clock.paused) return 'Control has paused the room.';
  const cost = h.cost(pub, t, role, cmd as never);
  if (cost > st.actions.available) return st.actions.available === 0 ? 'No actions left this turn.' : `That costs ${cost} actions.`;
  const reason = h.check(pub, t, role, cmd as never);
  return planning && reason === NO_SCANNER && scannerBox(pub, t, role, []) >= 0 ? null : reason;
}

export function reduce(state: SessionState, env: Envelope): ReduceResult {
  const { actor, cmd } = env;
  const metadata = cmd.type === 'SET_DRAFT' || cmd.type === 'SET_READY' || cmd.type === 'RESPOND_REFERRAL';
  const s: SessionState = actor.kind === 'seat' && metadata
    ? { ...state, tables: state.tables.map(t => t.no === actor.table ? structuredClone(t) : t) }
    : structuredClone(state);
  const rng = new Rng(s.rng);
  const ctx: Ctx = { s, events: [], rng, at: env.at, actor: env.actor };
  try {
    if (isSeatCommand(cmd)) {
      if (env.actor.kind !== 'seat') throw new Reject('Seat commands come from a seat.');
      const { table: tableNo, role } = env.actor;
      const t: TableState | undefined = s.tables.find((x) => x.no === tableNo);
      if (metadata) {
        runDraftCommand(ctx, tableNo, role, cmd);
        s.seq++;
        return { ok: true, state: s, events: ctx.events };
      }
      const h = HANDLERS[cmd.type];
      if (!['OPEN_CARD', 'CLOSE_CARD', 'MARK_DISPUTED'].includes(cmd.type)) return { ok: false, reason: 'Add this to your plan; it happens when the turn ends.' };
      const reason = checkSeatCommand(s, tableNo, role, cmd);
      if (reason) return { ok: false, reason };
      h!.run(ctx, t!, role, cmd as never);
    } else {
      if (env.actor.kind === 'seat') throw new Reject('That command is for Control.');
      runControl(ctx, cmd);
    }
    s.seq++;
    s.rng = rng.state;
    return { ok: true, state: s, events: ctx.events };
  } catch (e) {
    if (e instanceof Reject) return { ok: false, reason: e.message };
    return { ok: false, reason: 'That didn’t work — Control has been told.', bug: true, ...(e instanceof Error ? { error: e } : {}) } as ReduceResult;
  }
}
